<template>
    <div class="branch-selector"
         :class="{'branch-selector--with-btn-label': !!inBtnLabel}">
        <a data-analytics-id="Item Branch Selector"
           :class="mainBtnClasses"
           @click.prevent="showDropdown = !showDropdown">
            <span v-if="inBtnLabel"
                  class="branch-selector__btn-label">{{ l(inBtnLabel) }}</span>

            <i class="fa-solid fa-shop fa-fw branch-selector__branch-icon"></i>

            <span v-if="showName && value"
                  class="text">
                {{ truncateFilter(value.name, 20) }}
            </span>
            <span v-else-if="showName"
                  class="text">
                {{ l('NAV.NO_BRANCH_SELECTED') }}
            </span>
            <span v-else
                  class="text">
                {{ l('NAV.SELECT_BRANCH') }}
            </span>

            <i v-if="canChangeBranch"
               class="fa-solid fa-caret-down branch-selector__caret-icon"></i>
        </a>

        <div class="dropmenu"
             :class="{'dropmenu--show': showDropdown}"
             @click.prevent="showDropdown = false">
            <ul>
                <li v-for="branch in selectableBranches"
                    :key="branch.ID"
                    :class="{'active': value && value.ID === branch.ID}">
                    <a @click.prevent="change(branch)">
                        {{ branch.name }}
                    </a>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'BranchSelector',

    props: {
        /**
         * The currently selected value.
         */
        value: {
            type: Object,
            required: true,
        },

        /**
         * Display the branch name on the button (true), or "Select Branch" (false)?
         */
        showName: {
            type: Boolean,
            default: true,
        },

        /**
         * Classes to apply to the main "branch" button that shows the dropdown.
         */
        btnClasses: {
            type: String,
            default: 'btn btn-default btn-lg mr-0',
        },

        /**
         * If supplied, a lang key to display inside the branch selector button.
         */
        inBtnLabel: {
            type: String,
            default: null,
        },
    },

    emits: [
        'input',
    ],

    data() {
        return {
            showDropdown: false,
        };
    },

    computed: {
        ...mapState(['currentUser']),

        mainBtnClasses() {
            let classes = this.btnClasses;
            if (this.showDropdown) {
                classes += ' active';
            }

            return classes;
        },

        /**
         * @returns {Branch[]}
         */
        selectableBranches() {
            return this.currentUser.branches.filter((b) => b.isSelectable());
        },

        /**
         * @return {boolean}
         */
        canChangeBranch() {
            return this.selectableBranches.length > 1;
        },
    },

    methods: {
        /**
         * @param {Branch} newBranch
         */
        change(newBranch) {
            this.$emit('input', newBranch);

            // Close the dropdown after selecting in case they selected the same branch to close it.
            this.showDropdown = false;
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../less/variables.less";

// A .branch-selector (should) always be contained within a .item-branch-selector or .order-branch-selector element.
.branch-selector {
    position: relative;
    height: 100%;
    max-width: 100%;
    flex-grow: 0; // Don't expand inside a flex parent.

    > .btn {
        display: flex;
        align-items: center;
        justify-content: center;

        // Branch icon
        .branch-selector__branch-icon {
            margin-right: 5px;
            flex-grow: 0;
            flex-shrink: 0;
        }

        // Branch name
        > .text {
            flex-grow: 1;
            flex-shrink: 1;
            text-align: left;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        // Downward caret icon
        .branch-selector__caret-icon {
            margin-left: 5px;
            flex-grow: 0;
            flex-shrink: 0;
        }
    }

    &--with-btn-label {
        > .btn {
            padding-top: 9px !important;

            > .text {
                font-size: @font-size-medium;
            }
        }
    }

    &__btn-label {
        font-size: 9px;
        position: absolute;
        top: 2px;
        left: 0;
        right: 0;
        margin: 0;
        color: @dark-mid-grey;
    }

    .dropmenu {
        text-align: left;
    }
}
</style>
